import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import {
  FiAlertOctagon, FiArrowRight, FiCheckCircle, FiChevronDown, FiChevronRight, FiCopy, FiEdit2, FiRefreshCw,
} from "react-icons/fi";

import {
  ApiCallError, api, getONT, getOverview, listCPEDevices, listONTs, ontEvents, ontWAN, OVERVIEW_REFRESH_MS, updateONT,
  type WANConnection,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useSignalBands } from "@/lib/bands";
import { fmtDateTime, fmtNum, tStatus, useI18n, type Key } from "@/lib/i18n";
import { incidentTitle } from "@/lib/incident";
import { rxLevel } from "@/lib/olthealth";
import {
  causeKind, connState, cpeState, isMgmt, lastOutage, ontLive, stats, wanState, withGaps, type CpeState, type WanState,
} from "@/lib/ontstate";
import { fmtDbm } from "@/lib/signal";
import { cn } from "@/lib/utils";
import type { CPEDevice, ONTDetailResponse, ONTEvent, ONTHistoryResponse, RebootResponse } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { agoText, causeLabel, distText, durText, LiveBadge, RX_UI, RxTag } from "@/components/OntStatus";
import CustomerPingDialog from "@/components/CustomerPing";

type T = (k: Key, v?: Record<string, string | number>) => string;
const TABS = ["overview", "optical", "wan", "events", "technical"] as const;
type Tab = (typeof TABS)[number];
type Window = "1h" | "6h" | "24h" | "7d";

// Metric ids and units are data, never translated; only the human label is.
const METRICS: Array<{ id: string; label: Key; unit: string; rx?: boolean }> = [
  { id: "ont.rx_power_dbm", label: "metric.rxPower", unit: "dBm", rx: true },
  { id: "ont.olt_rx_dbm", label: "metric.oltRx", unit: "dBm", rx: true },
  { id: "ont.tx_power_dbm", label: "metric.txPower", unit: "dBm" },
  { id: "ont.temperature_c", label: "metric.temperature", unit: "°C" },
  { id: "ont.voltage_v", label: "metric.voltage", unit: "V" },
  { id: "ont.current_ma", label: "metric.current", unit: "mA" },
];

/** 409 = the CPE is already linked to another ONT; the server names it. */
function linkErrorText(t: T, e: Error): string {
  if (e instanceof ApiCallError && e.status === 409) return t("ontDetail.cpeConflict", { msg: e.message });
  return e.message;
}

function useNow() {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

function Panel({ title, right, children, className, tone }: {
  title: string; right?: ReactNode; children: ReactNode; className?: string; tone?: "crit" | "warn";
}) {
  return (
    <section className={cn("min-w-0 rounded-lg border bg-panel",
      tone === "crit" ? "border-signal-crit/60" : tone === "warn" ? "border-signal-warn/50" : "border-rule", className)}>
      <header className="flex items-center justify-between gap-3 border-b border-rule px-4 py-2">
        <h3 className={cn("eyebrow", tone === "crit" && "text-signal-crit", tone === "warn" && "text-signal-warn")}>{title}</h3>
        {right}
      </header>
      <div className="px-4 py-2.5">{children}</div>
    </section>
  );
}

function Row({ label, children, mono }: { label: string; children: ReactNode; mono?: boolean }) {
  return (
    // Values wrap rather than truncate: a translated or long answer must stay readable.
    <div className="grid grid-cols-[6.5rem_1fr] items-baseline gap-3 py-1">
      <dt className="text-[12px] text-slate-400">{label}</dt>
      <dd className={cn("min-w-0 break-words text-[13px] text-slate-100", mono && "data")}>{children}</dd>
    </div>
  );
}

function Dot({ tone }: { tone: "good" | "warn" | "crit" | "idle" }) {
  return <span className={cn("inline-block size-1.5 shrink-0 rounded-full",
    tone === "good" ? "bg-signal-good" : tone === "warn" ? "bg-signal-warn" : tone === "crit" ? "bg-signal-crit" : "bg-slate-600")} />;
}

const CPE_TONE: Record<CpeState, "good" | "warn" | "crit" | "idle"> = { online: "good", silent: "warn", unknown: "idle", none: "idle" };
const WAN_TONE: Record<WanState, "good" | "warn" | "crit" | "idle"> = { up: "good", down: "crit", unknown: "idle", none: "idle" };

function copy(text: string) {
  void navigator.clipboard?.writeText(text);
}

/** The address the customer browses from: a connected customer service (PPPoE first), never the TR-069 VLAN's unless it is all there is. */
function primaryWan(conns: WANConnection[] | undefined): WANConnection | undefined {
  const up = (conns ?? []).filter((c) => connState(c) === "up");
  const service = up.filter((c) => !isMgmt(c));
  const pool = service.length || (conns ?? []).some((c) => !isMgmt(c)) ? service : up;
  return pool.find((c) => c.mode === "PPPoE") ?? pool[0];
}

// ---------------------------------------------------------------------------

export default function ONTDetailPage() {
  const { t, locale } = useI18n();
  const { id = "" } = useParams<{ id: string }>();
  const [sp, setSp] = useSearchParams();
  const tab = (TABS as readonly string[]).includes(sp.get("tab") ?? "") ? (sp.get("tab") as Tab) : "overview";
  const now = useNow();
  const bands = useSignalBands();
  const qc = useQueryClient();
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "noc";
  const [pickerOpen, setPickerOpen] = useState(false);
  const [confirmReboot, setConfirmReboot] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [pinging, setPinging] = useState(false);
  const navigate = useNavigate();

  const detail = useQuery({ queryKey: ["ont", id], queryFn: () => getONT(id), refetchInterval: 30_000, enabled: !!id });
  // Live from the ACS; admin/NOC only (a PPPoE username is half a credential).
  const wan = useQuery({ queryKey: ["ont-wan", id], queryFn: () => ontWAN(id), enabled: canEdit && !!id, retry: false, refetchInterval: 60_000 });
  const ov = useQuery({ queryKey: ["overview"], queryFn: getOverview, refetchInterval: OVERVIEW_REFRESH_MS });
  const pollS = ov.data?.olt_poll_interval_s ?? 120;

  const linkCPE = useMutation({
    mutationFn: (cpe_device_id: number | null) => updateONT(id, { cpe_device_id }),
    onSuccess: () => { setPickerOpen(false); qc.invalidateQueries({ queryKey: ["ont", id] }); qc.invalidateQueries({ queryKey: ["ont-wan", id] }); },
  });
  // Only for an ONT the OLT no longer lists: a live one would be re-created by the next poll.
  const remove = useMutation({
    mutationFn: () => api<{ ok: true; alerts_closed: number }>(`/api/v1/onts/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["onts"] });
      qc.invalidateQueries({ queryKey: ["subscribers"] });
      qc.invalidateQueries({ queryKey: ["overview"] });
      navigate("/onts", { replace: true });
    },
  });
  const reboot = useMutation({
    mutationFn: () => api<RebootResponse>(`/api/v1/onts/${id}/reboot`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ont", id] }),
  });

  const d = detail.data;
  if (detail.isLoading) return <p className="text-slate-400">{t("common.loading")}</p>;
  if (!d) return <p className="text-signal-crit">{(detail.error as Error | null)?.message ?? t("common.error")}</p>;

  const ont = d.ont;
  const live = ontLive(ont, pollS, now);
  const up = live === "online" || live === "stale";
  const opt = d.optical;
  const optStale = !!opt && (now - Date.parse(opt.ts)) / 1000 > pollS * 3;
  const rxNow = opt?.rx_power_dbm ?? ont.rx_power_dbm ?? null;
  const lvl = up && !optStale ? rxLevel(rxNow, bands) : "nodata";
  const outage = lastOutage(ont, now);
  const cpe = d.cpe;
  const cState = cpeState(cpe, up, now);
  // Behind a dark ONT the ACS only has its last inform: WAN state is unknown, and
  // the IP is history ("last known"), never shown as the live path.
  const wState: WanState = !cpe ? "none" : !up || !canEdit || wan.isError ? "unknown" : wanState(wan.data?.linked ? wan.data.connections : undefined);
  const pWan = primaryWan(wan.data?.linked ? wan.data.connections : undefined);
  const lastWanIP = pWan?.external_ip || cpe?.wan_ip || null;
  // The live path shows the customer service's address when it is up; the
  // ACS-reported IP only when WAN state cannot be read at all (it is often the
  // TR-069 VLAN's). A down service shows no address rather than the management one.
  const wanIP = !up ? null : wState === "up" ? pWan?.external_ip || null : wState === "unknown" ? cpe?.wan_ip ?? null : null;
  const alerts = d.open_alerts;
  const critical = alerts.some((a) => a.severity === "critical");
  const name = ont.description || ont.serial;
  const ponHref = `/olts/${d.olt.id}?pon=${encodeURIComponent(d.pon_path)}`;
  const go = (k: Tab) => { const p = new URLSearchParams(sp); if (k === "overview") p.delete("tab"); else p.set("tab", k); setSp(p); };
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["ont", id] });
    qc.invalidateQueries({ queryKey: ["ont-wan", id] });
  };

  return (
    <div className="mx-auto max-w-[1600px] space-y-3">
      <nav className="flex items-center gap-1.5 text-[12px] text-slate-500">
        <Link to="/onts" className="hover:text-signal">{t("nav.onts")}</Link>
        <FiChevronRight className="size-3" />
        <Link to={`/olts/${d.olt.id}`} className="hover:text-signal">{d.olt.name}</Link>
        <FiChevronRight className="size-3" />
        <Link to={ponHref} className="data hover:text-signal">PON {d.pon_path}</Link>
        <FiChevronRight className="size-3" />
        <span className="data text-slate-300">ONT #{ont.ont_id}</span>
      </nav>

      {/* Header: who, where, and the one state that matters -- impossible to miss. */}
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="truncate text-xl font-semibold tracking-tight text-slate-100">{name}</h1>
            <LiveBadge live={live} big />
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px]">
            <button type="button" onClick={() => copy(ont.serial)} title={t("ont.act.copySerial")} className="data inline-flex items-center gap-1 text-slate-300 hover:text-signal">
              {ont.serial} <FiCopy className="size-3" />
            </button>
            <span className="text-slate-600">·</span>
            <span className="data text-slate-400">ONT #{ont.ont_id}</span>
            <span className="text-slate-600">·</span>
            <Link to={`/olts/${d.olt.id}`} className="text-slate-300 hover:text-signal">{d.olt.name}</Link>
            <FiArrowRight className="size-3 text-slate-600" />
            <Link to={ponHref} className="data text-slate-300 hover:text-signal">PON {d.pon_path}</Link>
            {cpe && (<><FiArrowRight className="size-3 text-slate-600" /><button type="button" onClick={() => go("wan")} className="text-slate-300 hover:text-signal">{cpe.model ?? "CPE"}</button></>)}
            {wanIP && (<><FiArrowRight className="size-3 text-slate-600" /><button type="button" onClick={() => go("wan")} className="data text-slate-300 hover:text-signal">{wanIP}</button></>)}
          </div>
          <p className="mt-1.5 text-[12.5px]">
            {up ? (
              <span className={live === "stale" ? "text-amber-200" : "text-slate-400"}>
                {live === "online" && <><span className="beacon mr-2 inline-block size-1.5 rounded-full bg-signal-good align-middle text-signal-good" /><span className="eyebrow mr-2 text-signal-good">{t("live.live")}</span></>}
                {t("ont.h.seen", { age: agoText(ont.last_seen_at, now) })}
                {ont.uptime_s !== null && ont.uptime_s !== undefined && <span className="text-slate-500"> · {t("ont.h.uptime", { d: durText(ont.uptime_s * 1000) })}</span>}
              </span>
            ) : (
              <span className="font-medium text-signal-crit">
                {outage?.kind ? `${causeLabel(outage.kind, t)} · ` : ""}{t("ont.h.offlineFor", { d: durText(outage?.durationMs ?? (ont.last_seen_at ? now - Date.parse(ont.last_seen_at) : null)) })}
                <span className="font-normal text-slate-400"> · {t("ont.h.seen", { age: agoText(ont.last_seen_at, now) })}</span>
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="secondary" onClick={refresh} disabled={detail.isFetching}><FiRefreshCw className={detail.isFetching ? "animate-spin" : ""} /> {t("ont.act.refresh")}</Button>
          <Button size="sm" variant="secondary" asChild><Link to={ponHref}>{t("ont.act.openPon")}</Link></Button>
          <MoreMenu canEdit={canEdit} hasCpe={!!cpe} manualLink={ont.cpe_device_id != null}
            removable={user?.role === "admin" && ont.run_state === "Missing"}
            onRestart={() => setConfirmReboot(true)} onLink={() => setPickerOpen(true)} onUnlink={() => linkCPE.mutate(null)}
            onCopy={() => copy(ont.serial)} onRemove={() => setConfirmRemove(true)} onPing={() => setPinging(true)} />
        </div>
      </header>

      {/* An ONT that is down gets an incident banner: the failure state dominates. */}
      {!up && (
        <div className="flex flex-wrap items-center gap-x-8 gap-y-2 rounded-lg border-2 border-signal-crit/70 bg-signal-crit/10 px-4 py-3">
          <div className="flex items-center gap-3">
            <FiAlertOctagon className="size-6 text-signal-crit" />
            <div>
              <div className="eyebrow text-signal-crit">{t("ont.inc.active")}</div>
              <div className="text-[15px] font-semibold text-slate-50">
                {t(live === "missing" ? "ont.inc.missing" : "ont.inc.offline")}
                {outage?.kind && <span className="font-normal text-slate-300"> · {causeLabel(outage.kind, t)}</span>}
              </div>
            </div>
          </div>
          <Fact label={t("ont.inc.started")} value={outage?.startedAt ? fmtDateTime(locale, outage.startedAt) : "—"} />
          <Fact label={t("ont.inc.duration")} value={durText(outage?.durationMs)} strong />
          <Fact label={t("ont.inc.lastRx")} value={rxNow === null ? "—" : fmtDbm(rxNow)} />
        </div>
      )}

      <section className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-3 xl:grid-cols-6">
        <Kpi label={t(up ? "ont.kpi.rx" : "ont.kpi.lastRx")} value={rxNow === null ? "—" : fmtDbm(rxNow)}
          tone={up && !optStale ? RX_UI[lvl].text : "text-slate-500"} sub={up && !optStale ? <RxTag level={lvl} /> : optStale ? <StaleTag at={opt?.ts} now={now} /> : null} />
        <Kpi label={t("onts.distance")} value={distText(d.distance_m)} />
        <Kpi label={t(up ? "common.lastSeen" : "ont.kpi.offlineFor")} value={up ? agoText(ont.last_seen_at, now) : durText(outage?.durationMs)}
          tone={up ? (live === "stale" ? "text-amber-200" : "text-slate-100") : "text-signal-crit"} />
        <Kpi word label="CPE" value={t(`ont.cpe.${cState}` as Key)} tone={cState === "online" ? "text-signal-good" : cState === "silent" ? "text-signal-warn" : "text-slate-400"} />
        <Kpi word label="WAN" value={t(`ont.wan.${wState}` as Key)} tone={wState === "up" ? "text-signal-good" : wState === "down" ? "text-signal-crit" : "text-slate-400"}
          sub={wanIP ? <span className="data text-[11px] text-slate-400">{wanIP}</span>
            : !up && lastWanIP ? <span className="data text-[11px] text-slate-500">{t("ont.wan.lastKnown", { ip: lastWanIP })}</span> : null} />
        <Kpi word={!alerts.length} label={t("ont.kpi.alerts")} value={alerts.length ? fmtNum(locale, alerts.length) : t("ont.kpi.none")}
          tone={critical ? "text-signal-crit" : alerts.length ? "text-signal-warn" : "text-slate-400"} />
      </section>

      <nav className="flex gap-1 overflow-x-auto border-b border-rule" role="tablist">
        {TABS.map((k) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => go(k)}
            className={cn("-mb-px border-b-2 px-3 py-2 text-[13px] transition-colors",
              tab === k ? "border-signal text-slate-100" : "border-transparent text-slate-400 hover:text-slate-200")}>
            {t(`ont.tab.${k}` as Key)}
          </button>
        ))}
      </nav>

      {tab === "overview" && (
        // Three columns from 1536px; below that two wider ones, incident kept in the first row.
        <div className="grid gap-3 lg:grid-cols-2 2xl:grid-cols-3">
          <OpticalPanel d={d} up={up} stale={optStale} now={now} bands={bands} onMore={() => go("optical")} />
          <IncidentPanel d={d} up={up} outage={outage} now={now} locale={locale} />
          <CustomerPanel d={d} cState={cState} wState={wState} wanIP={wanIP} lastWanIP={up ? null : lastWanIP} canEdit={canEdit} now={now} onLink={() => setPickerOpen(true)} onWan={() => go("wan")} />
          <PathPanel d={d} live={live} cState={cState} wState={wState} wanIP={wanIP} ponHref={ponHref} onCpe={() => go("wan")} />
          <RecentEvents events={d.events.slice(0, 5)} now={now} onAll={() => go("events")} />
          <NotesPanel id={id} notes={ont.notes} canEdit={canEdit} />
        </div>
      )}
      {tab === "optical" && <OpticalTab id={id} d={d} bands={bands} pollS={pollS} locale={locale} />}
      {tab === "wan" && <WanTab d={d} wan={wan.data} wanError={wan.error as Error | null} wanLoading={wan.isLoading} canEdit={canEdit} now={now} locale={locale}
        onLink={() => setPickerOpen(true)} onUnlink={() => linkCPE.mutate(null)} linkBusy={linkCPE.isPending} />}
      {tab === "events" && <EventsTab id={id} alerts={alerts} now={now} locale={locale} />}
      {tab === "technical" && <TechnicalTab d={d} locale={locale} />}

      {linkCPE.error && <p className="text-[12.5px] text-rose-300">{linkErrorText(t, linkCPE.error as Error)}</p>}
      {reboot.error && <p className="text-[12.5px] text-rose-300">{(reboot.error as Error).message}</p>}
      {reboot.data && (
        <p className="text-[12.5px] text-signal-good">{t(reboot.data.transport === "tr069" ? "ontDetail.rebootSentTr069" : "ontDetail.rebootSentOlt")}</p>
      )}

      <Dialog open={confirmReboot} onOpenChange={setConfirmReboot}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t(cpe ? "ont.act.restartTr069" : "ont.act.restartOlt")}</DialogTitle>
            <DialogDescription>{t("ont.act.restartWarn", { name })}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmReboot(false)}>{t("common.cancel")}</Button>
            <Button variant="destructive" disabled={reboot.isPending} onClick={() => { reboot.mutate(); setConfirmReboot(false); }}>
              {t(reboot.isPending ? "ontDetail.rebooting" : "ont.act.restartConfirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {pinging && <CustomerPingDialog kind="onts" id={ont.id} name={name} onClose={() => setPinging(false)} />}
      {confirmRemove && <RemoveDialog serial={ont.serial} name={name} pending={remove.isPending} error={remove.error as Error | null}
        onConfirm={() => remove.mutate()} onClose={() => setConfirmRemove(false)} />}

      <CPEPicker open={pickerOpen} onOpenChange={setPickerOpen} onPick={(cpeId) => linkCPE.mutate(cpeId)}
        pending={linkCPE.isPending} currentId={ont.cpe_device_id ?? null} error={linkCPE.error as Error | null} />
    </div>
  );
}

/** Irreversible, so the serial is typed back: it proves which ONT is meant. */
function RemoveDialog({ serial, name, pending, error, onConfirm, onClose }: {
  serial: string; name: string; pending: boolean; error: Error | null; onConfirm: () => void; onClose: () => void;
}) {
  const { t } = useI18n();
  const [typed, setTyped] = useState("");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("ont.rm.title", { name })}</DialogTitle>
          <DialogDescription>{t("ont.rm.body")}</DialogDescription>
        </DialogHeader>
        <ul className="list-disc space-y-0.5 pl-5 text-[12.5px] text-slate-300">
          <li>{t("ont.rm.alerts")}</li>
          <li>{t("ont.rm.history")}</li>
          <li>{t("ont.rm.links")}</li>
        </ul>
        <label className="block text-[12px] text-slate-400">
          {t("ont.rm.type", { serial })}
          <Input autoFocus value={typed} onChange={(e) => setTyped(e.target.value)} className="mt-1 font-mono" placeholder={serial} />
        </label>
        {error && <p className="text-[12.5px] text-rose-300">{error.message}</p>}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="destructive" disabled={pending || typed.trim().toUpperCase() !== serial.toUpperCase()} onClick={onConfirm}>{t("ont.rm.confirm")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Fact({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div>
      <div className="eyebrow">{label}</div>
      <div className={cn("data mt-0.5", strong ? "text-[18px] font-semibold text-signal-crit" : "text-[13px] text-slate-100")}>{value}</div>
    </div>
  );
}

function Kpi({ label, value, tone, sub, word }: { label: string; value: string; tone?: string; sub?: ReactNode; word?: boolean }) {
  return (
    <div className="min-w-0 bg-panel px-4 py-2.5">
      <div className="eyebrow truncate">{label}</div>
      {/* Words ("Tidak diketahui") wrap; numbers stay one line at full size. */}
      <div className={cn("mt-1 font-medium leading-tight", word ? "text-[1.05rem] break-words" : "data truncate text-[1.3rem]", tone ?? "text-slate-100")}>{value}</div>
      {sub && <div className="mt-0.5">{sub}</div>}
    </div>
  );
}

function StaleTag({ at, now }: { at: string | undefined; now: number }) {
  const { t } = useI18n();
  return (
    <span className="inline-flex items-center gap-1.5 text-[10.5px]">
      <span className="rounded-sm bg-amber-400/10 px-1 font-semibold tracking-wider text-amber-200">{t("olt.staleTag")}</span>
      <span className="text-slate-500">{agoText(at, now)}</span>
    </span>
  );
}

// --- More menu: disruptive actions are never primary buttons ------------------

function MoreMenu({ canEdit, hasCpe, manualLink, removable, onRestart, onLink, onUnlink, onCopy, onRemove, onPing }: {
  canEdit: boolean; hasCpe: boolean; manualLink: boolean; removable: boolean;
  onRestart: () => void; onLink: () => void; onUnlink: () => void; onCopy: () => void; onRemove: () => void; onPing: () => void;
}) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    window.addEventListener("mousedown", close);
    return () => window.removeEventListener("mousedown", close);
  }, [open]);
  const item = (label: string, fn: () => void, danger = false) => (
    <button type="button" onClick={() => { setOpen(false); fn(); }}
      className={cn("block w-full px-3 py-1.5 text-left text-[12.5px] hover:bg-white/[0.05]", danger ? "text-signal-crit" : "text-slate-200")}>
      {label}
    </button>
  );
  return (
    <div ref={ref} className="relative">
      <Button size="sm" variant="secondary" onClick={() => setOpen(!open)} aria-expanded={open}>{t("ont.act.more")} <FiChevronDown /></Button>
      {open && (
        <div className="absolute right-0 z-30 mt-1 w-56 overflow-hidden rounded-md border border-rule bg-panel py-1 shadow-xl shadow-black/50">
          {item(t("ont.act.copySerial"), onCopy)}
          {canEdit && item(t("cping.title"), onPing)}
          {canEdit && item(t(hasCpe ? "ontDetail.change" : "ontDetail.linkCpe"), onLink)}
          {canEdit && manualLink && item(t("ontDetail.unlink"), onUnlink)}
          {canEdit && <div className="my-1 border-t border-rule" />}
          {canEdit && item(t(hasCpe ? "ont.act.restartTr069" : "ont.act.restartOlt"), onRestart, true)}
          {removable && item(t("ont.rm.action"), onRemove, true)}
        </div>
      )}
    </div>
  );
}

// --- Overview panels ---------------------------------------------------------

function OpticalPanel({ d, up, stale, now, bands, onMore }: {
  d: ONTDetailResponse; up: boolean; stale: boolean; now: number; bands: ReturnType<typeof useSignalBands>; onMore: () => void;
}) {
  const { t } = useI18n();
  const o = d.optical;
  const current = up && !stale;
  // The OLT can answer "--" for an ONT that is up (its optical monitoring is
  // unreachable): the last real value is then shown, greyed, with its age.
  const side = (now_: number | null | undefined, last: { value: number; at: string } | null | undefined) =>
    now_ !== null && now_ !== undefined ? { v: now_, lastAt: null as string | null }
      : current && o && last ? { v: last.value, lastAt: last.at } : { v: null, lastAt: null };
  const a = side(o?.rx_power_dbm ?? (o ? null : d.ont.rx_power_dbm), d.last_reading?.rx);
  const b = side(o?.olt_rx_dbm ?? (o ? null : d.ont.olt_rx_dbm), d.last_reading?.olt_rx);
  const lv = (x: typeof a) => (current && !x.lastAt ? rxLevel(x.v, bands) : "nodata");
  return (
    <Panel title={t("ont.opt.title")} right={<button type="button" onClick={onMore} className="inline-flex items-center gap-1 text-[12px] text-slate-400 hover:text-signal">{t("ont.opt.history")} <FiArrowRight /></button>}>
      <div className="grid grid-cols-2 gap-4">
        {[
          { dir: t("ont.opt.down"), label: t("ont.opt.ontRx"), x: a },
          { dir: t("ont.opt.up"), label: t("ont.opt.oltRx"), x: b },
        ].map(({ dir, label, x }) => (
          <div key={dir}>
            <div className="eyebrow">{dir}</div>
            <div className="text-[11.5px] text-slate-500">{label}</div>
            <div className={cn("data mt-0.5 text-[1.35rem] leading-tight", current && !x.lastAt ? RX_UI[lv(x)].text : "text-slate-500")}>
              {x.v === null ? "—" : fmtDbm(x.v)}
            </div>
            {x.lastAt ? <span className="text-[11px] text-signal-warn" title={t("ont.opt.noReadingWhy")}>{t("ont.opt.noReading", { age: agoText(x.lastAt, now) })}</span>
              : current ? <RxTag level={lv(x)} /> : <span className="text-[11px] text-slate-500">{t(up ? "ont.opt.stale" : "ont.opt.lastKnown")}</span>}
          </div>
        ))}
      </div>
      <dl className="mt-2 border-t border-rule pt-1.5">
        <Row label={t("onts.distance")} mono>{distText(d.distance_m)}</Row>
        <Row label={t("ont.opt.thresholds")} mono>{t("ont.opt.bands", { w: bands.warn, c: bands.crit })}</Row>
        <Row label={t("ont.opt.sampled")} mono>{o ? agoText(o.ts, now) : "—"}</Row>
      </dl>
    </Panel>
  );
}

function CustomerPanel({ d, cState, wState, wanIP, lastWanIP, canEdit, now, onLink, onWan }: {
  d: ONTDetailResponse; cState: CpeState; wState: WanState; wanIP: string | null; lastWanIP: string | null; canEdit: boolean; now: number; onLink: () => void; onWan: () => void;
}) {
  const { t } = useI18n();
  const sub = d.subscriber;
  const cpe = d.cpe;
  return (
    <Panel title={t("ont.cust.title")} right={cpe && <button type="button" onClick={onWan} className="inline-flex items-center gap-1 text-[12px] text-slate-400 hover:text-signal">{t("ont.tab.wan")} <FiArrowRight /></button>}>
      <dl>
        <Row label={t("ontDetail.subscriber")}>
          {sub ? (
            <span><Link to={`/subscribers?open=${sub.id}`} className="text-slate-100 hover:text-signal">{sub.full_name}</Link>{sub.customer_code && <span className="data text-slate-500"> · {sub.customer_code}</span>}
              {sub.plan_code && <span className="text-slate-400"> · {sub.plan_code}</span>}</span>
          ) : (
            <span className="text-slate-500">{t("ont.cust.notLinked")} · <Link to="/subscribers" className="text-slate-300 hover:text-signal">{t("ont.cust.link")}</Link></span>
          )}
        </Row>
        {sub?.source === "whmcs" && (
          <Row label={t("ont.bill.title")}>
            <span className="inline-flex items-center gap-2"><Dot tone={BILL_TONE[sub.status] ?? "idle"} />
              {BILL_TONE[sub.status] ? t(`ont.bill.${sub.status}` as Key) : sub.status}
              {sub.pppoe_username && <span className="data text-slate-500">· {sub.pppoe_username}</span>}</span>
          </Row>
        )}
        <Row label="CPE">
          {cpe ? (
            <span className="inline-flex items-center gap-2"><Dot tone={CPE_TONE[cState]} />{t(`ont.cpe.${cState}` as Key)}
              <span className="text-slate-400">· {[cpe.manufacturer, cpe.model].filter(Boolean).join(" ") || "—"}</span></span>
          ) : (
            <span className="text-slate-500">{t("ontDetail.noCpe")}{canEdit && <> · <button type="button" onClick={onLink} className="text-slate-300 hover:text-signal">{t("ontDetail.linkCpe")}</button></>}</span>
          )}
        </Row>
        {cpe && (
          <Row label="ACS">
            <span className="inline-flex items-center gap-2"><Dot tone={cpe.acs_available ? "good" : "warn"} />{cpe.acs_server ?? "—"}
              <span className="text-slate-500">· {t("ont.cust.inform", { age: agoText(cpe.last_inform_at, now) })}</span></span>
          </Row>
        )}
        {cpe && (
          <Row label="WAN">
            <span className="inline-flex items-center gap-2"><Dot tone={WAN_TONE[wState]} />{t(`ont.wan.${wState}` as Key)}
              {wanIP ? <span className="data text-slate-300">· {wanIP}</span>
                : lastWanIP && <span className="data text-slate-500">· {t("ont.wan.lastKnown", { ip: lastWanIP })}</span>}</span>
          </Row>
        )}
      </dl>
      {sub?.status === "suspended" && (
        <p className="mt-2 rounded-sm border border-amber-400/40 bg-amber-400/10 px-2.5 py-1.5 text-[12.5px] text-amber-200">{t("ont.bill.suspendedNote")}</p>
      )}
    </Panel>
  );
}

// Billing words we translate; any other WHMCS status is shown as sent.
const BILL_TONE: Record<string, "good" | "warn" | "crit" | "idle"> = { active: "good", suspended: "warn", pending: "idle" };

function IncidentPanel({ d, up, outage, now, locale }: {
  d: ONTDetailResponse; up: boolean; outage: ReturnType<typeof lastOutage>; now: number; locale: string;
}) {
  const { t } = useI18n();
  const alerts = d.open_alerts;
  const crit = alerts.some((a) => a.severity === "critical");
  return (
    <Panel title={t(alerts.length ? "ont.inc.alertsTitle" : "ont.inc.lastOutage")} tone={crit ? "crit" : alerts.length ? "warn" : undefined}>
      {alerts.length > 0 && (
        <ul className="mb-2 space-y-1">
          {alerts.slice(0, 3).map((a) => (
            <li key={a.id} className="flex items-baseline gap-2 text-[13px]">
              <Dot tone={a.severity === "critical" ? "crit" : "warn"} />
              <span className="text-slate-100">{incidentTitle(t, a)}</span>
              {a.value !== null && a.metric.endsWith("_dbm") && <span className="data text-signal-warn">{fmtDbm(a.value)}</span>}
              <span className="ml-auto data text-[11px] text-slate-500">{agoText(a.opened_at, now)}</span>
            </li>
          ))}
        </ul>
      )}
      {alerts.length === 0 && up && <p className="mb-1.5 flex items-center gap-2 text-[12.5px] text-slate-400"><FiCheckCircle className="text-signal-good" /> {t("ont.inc.none")}</p>}
      {/* The last outage is history: dated and muted, never styled as a current alarm. */}
      <div className={cn(alerts.length > 0 && "border-t border-rule pt-2")}>
        {alerts.length > 0 && <div className="eyebrow mb-1">{t("ont.inc.lastOutage")}</div>}
        {!outage ? <p className="text-[12.5px] text-slate-500">{t("ont.inc.neverDown")}</p> : (
          <dl>
            <Row label={t("ont.inc.cause")}>
              <span className={outage.ongoing ? "text-signal-crit" : "text-slate-300"}>{causeLabel(outage.kind, t)}</span>
              {outage.raw && <span className="data ml-2 text-[11px] text-slate-500">{outage.raw}</span>}
            </Row>
            <Row label={t("ont.inc.started")} mono>{outage.startedAt ? `${fmtDateTime(locale, outage.startedAt)} · ${agoText(outage.startedAt, now)}` : "—"}</Row>
            <Row label={t("ont.inc.duration")} mono>
              {outage.ongoing ? <span className="text-signal-crit">{t("ont.inc.ongoing", { d: durText(outage.durationMs) })}</span> : durText(outage.durationMs)}
            </Row>
          </dl>
        )}
      </div>
    </Panel>
  );
}

function PathPanel({ d, live, cState, wState, wanIP, ponHref, onCpe }: {
  d: ONTDetailResponse; live: ReturnType<typeof ontLive>; cState: CpeState; wState: WanState; wanIP: string | null; ponHref: string; onCpe: () => void;
}) {
  const { t } = useI18n();
  const steps: { label: string; value: ReactNode; sub?: string; tone: "good" | "warn" | "crit" | "idle"; to?: string; onClick?: () => void }[] = [
    { label: "OLT", value: d.olt.name, sub: d.olt.mgmt_ip, tone: d.olt.status === "reachable" ? "good" : d.olt.status === "unreachable" ? "crit" : "warn", to: `/olts/${d.olt.id}` },
    { label: "PON", value: <span className="data">{d.pon_path}</span>, tone: "idle", to: ponHref },
    { label: "ONT", value: <span className="data">#{d.ont.ont_id} · {d.ont.serial}</span>, tone: live === "online" ? "good" : live === "stale" ? "warn" : "crit" },
    { label: "CPE", value: d.cpe ? [d.cpe.manufacturer, d.cpe.model].filter(Boolean).join(" ") || "CPE" : t("ont.path.noCpe"), tone: CPE_TONE[cState], onClick: d.cpe ? onCpe : undefined },
    { label: "WAN", value: <span className="data">{wanIP ?? "—"}</span>, tone: WAN_TONE[wState], onClick: d.cpe ? onCpe : undefined },
  ];
  return (
    <Panel title={t("ont.path.title")}>
      <ol>
        {steps.map((s, i) => {
          const body = (
            <span className="flex min-w-0 items-center gap-2.5">
              <span className="eyebrow w-9 shrink-0">{s.label}</span>
              <Dot tone={s.tone} />
              <span className="truncate text-[13px] text-slate-100">{s.value}</span>
              {s.sub && <span className="data truncate text-[11px] text-slate-500">{s.sub}</span>}
            </span>
          );
          return (
            <li key={s.label} className="relative py-1">
              {i > 0 && <span className="absolute -top-1.5 left-[3.35rem] h-2.5 border-l border-rule" aria-hidden />}
              {s.to ? <Link to={s.to} className="block hover:[&_span]:text-signal">{body}</Link>
                : s.onClick ? <button type="button" onClick={s.onClick} className="block w-full text-left hover:[&_span]:text-signal">{body}</button>
                : body}
            </li>
          );
        })}
      </ol>
    </Panel>
  );
}

export function eventTitle(e: ONTEvent, t: T): { title: string; tone: "good" | "warn" | "crit" | "idle"; detail: string | null } {
  if (e.next === "reboot") return { title: t("ontDetail.rebootedByOperator"), tone: "idle", detail: null };
  const k = causeKind(e.detail);
  switch (e.event) {
    case "online": return { title: t("olt.ev.online"), tone: "good", detail: null };
    case "offline": return { title: t("olt.ev.offline"), tone: "crit", detail: k ? causeLabel(k, t) : e.detail };
    case "down_cause": return { title: t("olt.ev.down_cause"), tone: "warn", detail: k ? causeLabel(k, t) : e.detail };
    case "discovered": return { title: t("olt.ev.discovered"), tone: "idle", detail: null };
    default: return { title: e.detail ?? t("olt.ev.alarm"), tone: "idle", detail: null };
  }
}

function RecentEvents({ events, now, onAll }: { events: ONTEvent[]; now: number; onAll: () => void }) {
  const { t } = useI18n();
  return (
    <Panel title={t("ont.ev.recent")} right={<button type="button" onClick={onAll} className="inline-flex items-center gap-1 text-[12px] text-slate-400 hover:text-signal">{t("ont.ev.all")} <FiArrowRight /></button>}>
      {events.length === 0 ? <p className="text-[12.5px] text-slate-500">{t("ontDetail.noEvents")}</p> : (
        <ol>
          {events.map((e) => {
            const x = eventTitle(e, t);
            const ts = e.occurred_at ?? e.created_at;
            return (
              <li key={e.id} className="grid grid-cols-[auto_1fr_auto] items-baseline gap-2.5 py-1">
                <Dot tone={x.tone} />
                <span className="min-w-0 truncate text-[13px] text-slate-100">{x.title}{x.detail && <span className="text-slate-400"> · {x.detail}</span>}</span>
                <span className="data text-[11px] text-slate-500" title={ts}>{agoText(ts, now)}</span>
              </li>
            );
          })}
        </ol>
      )}
    </Panel>
  );
}

function NotesPanel({ id, notes, canEdit }: { id: string; notes: string | null; canEdit: boolean }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (n: string) => updateONT(id, { notes: n.trim() || null }),
    onSuccess: () => { setDraft(null); qc.invalidateQueries({ queryKey: ["ont", id] }); qc.invalidateQueries({ queryKey: ["onts"] }); },
  });
  return (
    <Panel title={t("common.notes")} right={canEdit && draft === null && (
      <button type="button" onClick={() => setDraft(notes ?? "")} className="inline-flex items-center gap-1 text-[12px] text-slate-400 hover:text-signal">
        <FiEdit2 /> {t(notes ? "common.edit" : "ont.notes.add")}
      </button>
    )}>
      {draft === null ? (
        notes ? <p className="line-clamp-4 whitespace-pre-wrap text-[13px] text-slate-200">{notes}</p>
          : <p className="text-[12.5px] text-slate-500">{t("ont.notes.none")}</p>
      ) : (
        <div>
          <textarea autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={t("ontDetail.notesPlaceholder")}
            className="min-h-20 w-full rounded-md border border-rule bg-background p-2 text-[13px] text-slate-200 outline-none focus:border-signal/60" />
          <div className="mt-1.5 flex gap-2">
            <Button size="xs" onClick={() => save.mutate(draft)} disabled={save.isPending}>{t(save.isPending ? "common.saving" : "common.save")}</Button>
            <Button size="xs" variant="ghost" onClick={() => setDraft(null)} disabled={save.isPending}>{t("common.cancel")}</Button>
            {save.error && <span className="text-[12px] text-rose-300">{(save.error as Error).message}</span>}
          </div>
        </div>
      )}
    </Panel>
  );
}

// --- Tabs --------------------------------------------------------------------

function OpticalTab({ id, d, bands, pollS, locale }: { id: string; d: ONTDetailResponse; bands: ReturnType<typeof useSignalBands>; pollS: number; locale: string }) {
  const { t } = useI18n();
  const [metric, setMetric] = useState(METRICS[0].id);
  const [win, setWin] = useState<Window>("24h");
  const m = METRICS.find((x) => x.id === metric)!;
  const history = useQuery({
    queryKey: ["ont", id, "history", metric, win],
    queryFn: () => api<ONTHistoryResponse>(`/api/v1/onts/${id}/history?metric=${metric}&window=${win}`),
    refetchInterval: 60_000,
  });
  const peers = useQuery({
    queryKey: ["olt-onts", String(d.olt.id), d.pon_path],
    queryFn: () => listONTs({ oltId: d.olt.id, pon: d.pon_path, limit: 500 }),
    staleTime: 60_000,
  });
  // A hole wherever samples stop for more than three polls: offline stretches are gaps, not lines.
  const data = useMemo(() => withGaps(history.data?.points ?? [], pollS * 3 * 1000), [history.data, pollS]);
  const s = stats(data);
  const lo = s ? Math.min(s.min, m.rx ? bands.warn : s.min) : 0;
  const hi = s ? s.max : 0;
  const pad = Math.max(0.5, (hi - lo) * 0.1);
  const peer = (peers.data?.onts ?? []).filter((o) => o.run_state === "Online" && o.rx_power_dbm !== null);
  const peerAvg = peer.length ? peer.reduce((a, o) => a + (o.rx_power_dbm ?? 0), 0) / peer.length : null;
  const rank = d.ont.rx_power_dbm !== null && d.ont.rx_power_dbm !== undefined
    ? [...peer].sort((a, b) => (a.rx_power_dbm ?? 0) - (b.rx_power_dbm ?? 0)).findIndex((o) => o.id === d.ont.id) + 1 : 0;
  const fmtV = (v: number | null | undefined) => (v === null || v === undefined ? "—" : `${v.toFixed(2)} ${m.unit}`);
  const o = d.optical;

  return (
    <div className="grid gap-3 xl:grid-cols-[1fr_20rem]">
      <Panel title={t("ontDetail.opticalHistory")} right={
        <div className="flex items-center gap-2">
          <select value={metric} onChange={(e) => setMetric(e.target.value)} className="h-7 rounded-sm border border-rule bg-panel px-2 text-[12px] text-slate-300" aria-label="metric">
            {METRICS.map((x) => <option key={x.id} value={x.id}>{t(x.label)}</option>)}
          </select>
          {(["1h", "6h", "24h", "7d"] as Window[]).map((w) => (
            <Button key={w} size="xs" variant={w === win ? "default" : "secondary"} onClick={() => setWin(w)}>{w}</Button>
          ))}
        </div>
      }>
        <div className="mb-2 flex flex-wrap gap-x-6 gap-y-1 text-[12px]">
          {[["ont.opt.current", s?.last], ["ont.opt.avg", s?.avg], ["ont.opt.min", s?.min], ["ont.opt.max", s?.max]].map(([k, v]) => (
            <span key={k as string}><span className="text-slate-500">{t(k as Key)}</span> <span className="data text-slate-100">{fmtV(v as number | undefined)}</span></span>
          ))}
        </div>
        <div className="h-72">
          {history.isLoading ? <p className="text-[12.5px] text-slate-500">{t("common.loading")}</p> : !s ? (
            <p className="flex h-full items-center justify-center text-[12.5px] text-slate-500">{t("ontDetail.noSamples")}</p>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                <CartesianGrid stroke="#182033" strokeDasharray="2 4" />
                <XAxis dataKey="ts" type="number" scale="time" domain={["dataMin", "dataMax"]} stroke="#64748b" fontSize={11}
                  tickFormatter={(v) => new Date(v).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })} />
                <YAxis stroke="#64748b" fontSize={11} width={52} domain={[lo - pad, hi + pad]} tickFormatter={(v) => Number(v).toFixed(1)} />
                <Tooltip labelFormatter={(v) => fmtDateTime(locale, new Date(v as number).toISOString())}
                  formatter={(v) => [v === null ? "—" : `${Number(v).toFixed(2)} ${m.unit}`, t(m.label)]}
                  contentStyle={{ background: "#0a0f1a", border: "1px solid #182033", fontSize: 12 }} />
                {m.rx && <ReferenceLine y={bands.warn} stroke="var(--color-signal-warn)" strokeDasharray="4 4" label={{ value: `${t("ont.rx.weak")} ${bands.warn}`, fill: "#f59e0b", fontSize: 10, position: "insideTopRight" }} />}
                {m.rx && bands.crit >= lo - pad && <ReferenceLine y={bands.crit} stroke="var(--color-signal-crit)" strokeDasharray="4 4" label={{ value: `${t("ont.rx.critical")} ${bands.crit}`, fill: "#ef4444", fontSize: 10, position: "insideBottomRight" }} />}
                <Line type="monotone" dataKey="value" stroke="#22d3ee" dot={false} strokeWidth={1.75} connectNulls={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
        <p className="mt-1 text-[11.5px] text-slate-500">{t("ont.opt.gapNote")}</p>
      </Panel>
      <div className="space-y-3">
        <Panel title={t("ont.opt.now")}>
          <dl>
            <Row label={t("ont.opt.ontRx")} mono>{fmtDbm(o?.rx_power_dbm ?? null)}</Row>
            <Row label={t("ont.opt.oltRx")} mono>{fmtDbm(o?.olt_rx_dbm ?? null)}</Row>
            <Row label={t("metric.txPower")} mono>{fmtDbm(o?.tx_power_dbm ?? null)}</Row>
            <Row label={t("metric.temperature")} mono>{o?.temperature_c === null || o?.temperature_c === undefined ? "—" : `${o.temperature_c.toFixed(1)} °C`}</Row>
            <Row label={t("metric.voltage")} mono>{o?.voltage_v === null || o?.voltage_v === undefined ? "—" : `${o.voltage_v.toFixed(2)} V`}</Row>
            <Row label={t("onts.distance")} mono>{distText(d.distance_m)}</Row>
          </dl>
        </Panel>
        <Panel title={t("ont.opt.ponCompare", { pon: d.pon_path })}>
          {peerAvg === null ? <p className="text-[12.5px] text-slate-500">{t("common.loading")}</p> : (
            <dl>
              <Row label={t("ont.opt.ponAvg")} mono>{fmtDbm(peerAvg)}</Row>
              <Row label={t("ont.opt.thisOnt")} mono>{fmtDbm(d.ont.rx_power_dbm ?? null)}</Row>
              {rank > 0 && <Row label={t("ont.opt.rank")}>{t("ont.opt.rankOf", { r: rank, n: peer.length })}</Row>}
            </dl>
          )}
        </Panel>
      </div>
    </div>
  );
}

function WanTab({ d, wan, wanError, wanLoading, canEdit, now, locale, onLink, onUnlink, linkBusy }: {
  d: ONTDetailResponse; wan: Awaited<ReturnType<typeof ontWAN>> | undefined; wanError: Error | null; wanLoading: boolean; canEdit: boolean;
  now: number; locale: string; onLink: () => void; onUnlink: () => void; linkBusy: boolean;
}) {
  const { t } = useI18n();
  const cpe = d.cpe;
  const macs = d.mac_addresses ?? [];
  const macRow = (mac: string, note?: string) => (
    <span className="inline-flex items-center gap-2">
      <span className="data">{mac}</span>{note && <span className="text-[11px] text-slate-500">{note}</span>}
      <button type="button" onClick={() => copy(mac)} title={t("ont.act.copy")} className="text-slate-500 hover:text-signal"><FiCopy className="size-3" /></button>
    </span>
  );
  return (
    <div className="grid gap-3 xl:grid-cols-[22rem_1fr]">
      <Panel title="CPE / GenieACS" right={canEdit && (
        <span className="flex gap-3 text-[12px]">
          <button type="button" onClick={onLink} disabled={linkBusy} className="text-slate-400 hover:text-signal">{t(cpe ? "ontDetail.change" : "ontDetail.linkCpe")}</button>
          {cpe && d.ont.cpe_device_id != null && <button type="button" onClick={onUnlink} disabled={linkBusy} className="text-slate-400 hover:text-signal">{t("ontDetail.unlink")}</button>}
        </span>
      )}>
        {!cpe ? <p className="text-[12.5px] text-slate-500">{t("ontDetail.noCpe")}</p> : (
          <dl>
            <Row label={t("ontDetail.manufacturer")}>{cpe.manufacturer ?? "—"}</Row>
            <Row label={t("olts.model")}>{cpe.model ?? "—"}</Row>
            <Row label={t("ontDetail.acsServer")}>{cpe.acs_server ?? "—"}{!cpe.acs_available && <span className="ml-2 text-signal-warn">{t("ont.cpe.acsDown")}</span>}</Row>
            <Row label={t("ontDetail.lastInform")} mono>{cpe.last_inform_at ? `${agoText(cpe.last_inform_at, now)} · ${fmtDateTime(locale, cpe.last_inform_at)}` : "—"}</Row>
            <Row label={t("ontDetail.lastBoot")} mono>{fmtDateTime(locale, cpe.last_boot_at)}</Row>
            <Row label={t("ontDetail.wanIp")} mono>{cpe.wan_ip ?? "—"}</Row>
            <Row label={t("ontDetail.wanMac")}>{cpe.mac ? macRow(cpe.mac) : "—"}</Row>
            <Row label={t("ontDetail.matchedBy")}>{tStatus(t, cpe.link_source)}</Row>
            <Row label={t("ontDetail.deviceId")} mono><span title={cpe.genieacs_id}>{cpe.genieacs_id}</span></Row>
          </dl>
        )}
      </Panel>
      <div className="space-y-3">
        <Panel title={t("wan.title")} right={wan?.linked && wan.last_inform ? <span className="text-[11.5px] text-slate-500">{t("wan.lastInform")} {fmtDateTime(locale, wan.last_inform)}</span> : null}>
          {!canEdit ? <p className="text-[12.5px] text-slate-500">{t("ont.wan.roleHidden")}</p>
            : wanLoading ? <p className="text-[12.5px] text-slate-500">{t("common.loading")}</p>
            : wanError ? <p className="text-[12.5px] text-signal-warn">{t("wan.unavailable")}</p>
            : !wan?.linked ? <p className="text-[12.5px] text-slate-500">{t("wan.notLinked")}</p>
            : wan.connections.length === 0 ? <p className="text-[12.5px] text-slate-500">{t("wan.none")}</p> : (
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left">
                    {[t("wan.service"), t("wan.mode"), "VLAN", "IP", t("wan.detail"), t("common.status")].map((h) => <th key={h} className="eyebrow pb-1.5 font-medium">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {wan.connections.map((c) => {
                    const st = connState(c);
                    return (
                      <tr key={c.path} className="border-t border-rule/60">
                        <td className="data py-1.5 pr-3 text-slate-100">{c.name || "—"}</td>
                        <td className="py-1.5 pr-3 text-slate-300">{c.mode}</td>
                        <td className="data py-1.5 pr-3 text-slate-300">{c.vlan ?? "—"}</td>
                        <td className="data py-1.5 pr-3 text-slate-100">{c.external_ip || "—"}</td>
                        <td className="py-1.5 pr-3 text-slate-400">{c.mode === "PPPoE" ? c.username || "—" : c.addressing || c.connection_type || "—"}</td>
                        <td className="py-1.5">
                          <span className={cn("inline-flex items-center gap-1.5 text-[11.5px] font-semibold uppercase tracking-wider",
                            st === "up" ? "text-signal-good" : st === "down" ? "text-signal-crit" : "text-slate-500")}>
                            <Dot tone={st === "up" ? "good" : st === "down" ? "crit" : "idle"} />{t(`ont.conn.${st}` as Key)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          <p className="mt-2 text-[11.5px] text-slate-500">{t("wan.readOnly")}</p>
        </Panel>
        <Panel title={t("ontDetail.learnedMacs")} right={<span className="text-[11.5px] text-slate-500">{t("ontDetail.macsFrom", { n: fmtNum(locale, macs.length) })}</span>}>
          {macs.length === 0 ? <p className="text-[12.5px] text-slate-500">{t("ont.mac.none")}</p> : (
            <ul className="flex flex-wrap gap-x-6 gap-y-1.5 text-[13px] text-slate-100">
              {macs.map((mm) => <li key={`${mm.mac}-${mm.vlan ?? ""}`}>{macRow(mm.mac, `${t("ontDetail.vlan")} ${mm.vlan ?? "—"}`)}</li>)}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}

function EventsTab({ id, alerts, now, locale }: { id: string; alerts: ONTDetailResponse["open_alerts"]; now: number; locale: string }) {
  const { t } = useI18n();
  const q = useQuery({ queryKey: ["ont", id, "events"], queryFn: () => ontEvents(id, 200), refetchInterval: 30_000 });
  const items = q.data?.items ?? [];
  const dayFmt = new Intl.DateTimeFormat(locale, { weekday: "short", day: "numeric", month: "short" });
  const timeFmt = new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit" });
  let lastDay = "";
  return (
    <div className="grid gap-3 xl:grid-cols-[1fr_22rem]">
      <Panel title={t("ont.ev.history")}>
        {q.isLoading && <p className="text-[12.5px] text-slate-500">{t("common.loading")}</p>}
        {!q.isLoading && items.length === 0 && <p className="text-[12.5px] text-slate-500">{t("ontDetail.noEvents")}</p>}
        <ol>
          {items.map((e) => {
            const ts = e.occurred_at ?? e.created_at;
            const day = dayFmt.format(new Date(ts));
            const header = day !== lastDay;
            lastDay = day;
            const x = eventTitle(e, t);
            return (
              <li key={e.id}>
                {header && <div className="eyebrow mt-2.5 mb-1 first:mt-0">{day}</div>}
                <div className="grid grid-cols-[3.5rem_auto_1fr_auto] items-baseline gap-3 border-t border-rule/50 py-1.5">
                  <span className="data text-[12px] text-slate-500">{timeFmt.format(new Date(ts))}</span>
                  <Dot tone={x.tone} />
                  <span className="min-w-0 text-[13px] text-slate-100">
                    {x.title}{x.detail && <span className="text-slate-400"> · {x.detail}</span>}
                    {e.detail && causeKind(e.detail) && <span className="data ml-2 text-[11px] text-slate-500">{e.detail}</span>}
                    {e.source === "alarm" && <span className="ml-2 text-[11px] text-slate-500">{t("ont.ev.oltReported")}</span>}
                  </span>
                  <span className="data text-[11px] text-slate-600">{agoText(ts, now)}</span>
                </div>
              </li>
            );
          })}
        </ol>
      </Panel>
      <Panel title={t("overview.openAlerts")}>
        {alerts.length === 0 ? <p className="text-[12.5px] text-slate-500">{t("ontDetail.noOpenAlerts")}</p> : (
          <ul className="space-y-1.5">
            {alerts.map((a) => (
              <li key={a.id} className="grid grid-cols-[auto_1fr_auto] items-baseline gap-2 text-[13px]">
                <Dot tone={a.severity === "critical" ? "crit" : "warn"} />
                <span className="text-slate-100">{incidentTitle(t, a)}{a.value !== null && a.metric.endsWith("_dbm") && <span className="data text-signal-warn"> {fmtDbm(a.value)}</span>}</span>
                <span className="data text-[11px] text-slate-500">{agoText(a.opened_at, now)}</span>
              </li>
            ))}
          </ul>
        )}
        <Link to="/alerts" className="mt-2 inline-flex items-center gap-1 text-[12px] text-slate-400 hover:text-signal">{t("nav.alerts")} <FiArrowRight /></Link>
      </Panel>
    </div>
  );
}

function TechnicalTab({ d, locale }: { d: ONTDetailResponse; locale: string }) {
  const { t } = useI18n();
  const o = d.ont;
  const fmt = (iso?: string | null) => fmtDateTime(locale, iso);
  return (
    <div className="grid gap-3 lg:grid-cols-2 xl:grid-cols-3">
      <Panel title={t("ont.tech.identity")}>
        <dl>
          <Row label={t("onts.serial")} mono>{o.serial}</Row>
          <Row label={t("olts.vendor")}>{o.vendor ?? "—"}</Row>
          <Row label={t("olts.model")}>{o.model ?? "—"}</Row>
          <Row label={t("ontDetail.onuType")}>{o.onu_type ?? "—"}</Row>
          <Row label={t("onts.number")} mono>{o.ont_id}</Row>
          <Row label={t("ont.tech.internalId")} mono>{o.id}</Row>
        </dl>
      </Panel>
      <Panel title={t("ont.tech.provisioning")}>
        <dl>
          <Row label={t("ontDetail.runState")} mono>{o.run_state ?? "—"}</Row>
          <Row label={t("ontDetail.configState")} mono>{o.config_state ?? "—"}</Row>
          <Row label={t("ontDetail.matchState")} mono>{o.match_state ?? "—"}</Row>
          <Row label={t("ontDetail.lastDownCause")} mono>{o.last_down_cause ?? "—"}</Row>
          <Row label={t("ontDetail.lastUp")} mono>{fmt(o.last_up_at)}</Row>
          <Row label={t("ontDetail.lastDown")} mono>{fmt(o.last_down_at)}</Row>
          <Row label={t("ontDetail.lastDyingGasp")} mono>{fmt(o.last_dying_gasp_at)}</Row>
        </dl>
      </Panel>
      <Panel title={t("ontDetail.ponPath")}>
        <dl>
          <Row label="OLT">{d.olt.name}</Row>
          <Row label={t("ontDetail.oltIp")} mono>{d.olt.mgmt_ip ?? "—"}</Row>
          <Row label={t("ontDetail.oltStatus")}>{tStatus(t, d.olt.status)}</Row>
          <Row label={t("ontDetail.index")} mono>{d.pon_port.olt_index}</Row>
          <Row label="F/S/P" mono>{d.pon_port.frame}/{d.pon_port.slot}/{d.pon_port.port}</Row>
          <Row label={t("olts.driver")} mono>{d.olt.driver ?? "—"}</Row>
          <Row label={t("ont.opt.sampled")} mono>{fmt(d.optical?.ts)}</Row>
          <Row label={t("metric.current")} mono>{d.optical?.current_ma === null || d.optical?.current_ma === undefined ? "—" : `${d.optical.current_ma} mA`}</Row>
        </dl>
      </Panel>
    </div>
  );
}

// --- CPE picker (unchanged behaviour) ----------------------------------------

function CPEPicker({ open, onOpenChange, onPick, pending, currentId, error }: {
  open: boolean; onOpenChange: (v: boolean) => void; onPick: (id: number) => void; pending: boolean; currentId: number | null; error: Error | null;
}) {
  const { t, locale } = useI18n();
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => {
    const h = setTimeout(() => setQ(text), 300);
    return () => clearTimeout(h);
  }, [text]);
  const list = useQuery({ queryKey: ["cpe-devices", q], queryFn: () => listCPEDevices(q), enabled: open });
  const items: CPEDevice[] = list.data?.items ?? [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("ontDetail.pickerTitle")}</DialogTitle>
          <DialogDescription>{t("ontDetail.pickerDesc")}</DialogDescription>
        </DialogHeader>
        <Input autoFocus placeholder={t("ontDetail.pickerPlaceholder")} value={text} onChange={(e) => setText(e.target.value)} className="h-8 text-xs" />
        {list.error && <p className="text-sm text-rose-300">{(list.error as Error).message}</p>}
        {error && <p className="text-sm text-rose-300">{linkErrorText(t, error)}</p>}
        <div className="max-h-80 divide-y divide-slate-800 overflow-auto text-sm">
          {list.isLoading && <p className="py-3 text-slate-400">{t("common.loading")}</p>}
          {!list.isLoading && items.length === 0 && <p className="py-3 text-slate-500">{t("ontDetail.noCpeMatch")}</p>}
          {items.map((c) => (
            <div key={c.id} className="flex items-center gap-3 py-2">
              <div className="min-w-0 flex-1">
                <p className="truncate font-mono text-xs" title={c.genieacs_id}>{c.serial || c.genieacs_id}</p>
                <p className="truncate text-xs text-slate-400">{[c.model, c.mac, c.wan_ip].filter(Boolean).join(" · ") || "—"}</p>
              </div>
              <span className="shrink-0 text-xs text-slate-500">{fmtDateTime(locale, c.last_inform_at)}</span>
              <Button size="sm" variant="outline" onClick={() => onPick(c.id)} disabled={pending || c.id === currentId}>
                {t(c.id === currentId ? "ontDetail.linked" : "ontDetail.link")}
              </Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
